import React, { Component } from "react";
import * as Icon from "react-feather";

import ToolsCheckbox from "../clients/Tools/kit/ToolsCheckbox";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import SkillLibraryService from "../../services/SkillLibraryService";
import ToolLibraryService from "../../services/ToolLibraryService";
import AgentSourceIcon from "./AgentSourceIcon";

import "./agent-skill-editor.css";

class AgentSkillEditor extends Component {
	onFieldChanged = ({ field, event }) => {
		this.props.onChange({ [field]: event.target.value });
	};

	onToolToggled = ({ toolName }) => {
		const { draft, onChange } = this.props;
		const isChosen = draft.toolNames.includes(toolName);
		const toolNames = isChosen ? draft.toolNames.filter(name => name !== toolName) : [...draft.toolNames, toolName];
		const reads = isChosen ? (draft.reads || []).filter(read => read.tool !== toolName) : draft.reads || [];
		onChange({ toolNames, reads });
	};

	onReadFirstToggled = ({ toolName }) => {
		const { draft, onChange } = this.props;
		const reads = draft.reads || [];
		const isReadFirst = reads.some(read => read.tool === toolName);
		const nextReads = isReadFirst ? reads.filter(read => read.tool !== toolName) : [...reads, { tool: toolName }];
		onChange({ reads: nextReads });
	};

	renderTool = tool => {
		const { draft, isSaving } = this.props;
		const isChosen = draft.toolNames.includes(tool.name);
		const isReadFirst = (draft.reads || []).some(read => read.tool === tool.name);
		const label = (
			<span className="agent-skill-editor__tool-text">
				<span className="agent-skill-editor__tool-name">{tool.label}</span>
				{tool.description && <span className="agent-skill-editor__tool-description">{tool.description}</span>}
			</span>
		);
		return (
			<div key={tool.name} className="agent-skill-editor__tool-row">
				<ToolsCheckbox
					checked={isChosen}
					onChange={() => this.onToolToggled({ toolName: tool.name })}
					label={label}
					disabled={isSaving}
					containerClass="agent-skill-editor__tool"
				/>
				{isChosen && tool.canReadUpFront && (
					<ToolsCheckbox
						checked={isReadFirst}
						onChange={() => this.onReadFirstToggled({ toolName: tool.name })}
						label="Read before the run starts, so the agent begins with the result"
						disabled={isSaving}
						containerClass="agent-skill-editor__read-first"
					/>
				)}
			</div>
		);
	};

	renderToolGroup = group => {
		return (
			<div key={group.key || "always"} className="agent-skill-editor__tool-group">
				<div className="agent-skill-editor__tool-group-head">
					{group.key ? <AgentSourceIcon icon={group.key} size="small" /> : <Icon.Box size={13} />}
					{group.label}
				</div>
				{group.tools.map(this.renderTool)}
			</div>
		);
	};

	render() {
		const { draft, original, catalogue, categories, isNew, isSaving, saveError, onSave, onCancel, onChange } = this.props;
		const { pendingReview, authorizationReason, onAuthorizationReasonChange, onAuthorize, onDismissReview } = this.props;
		const isVersionChanged = isNew || SkillLibraryService.isSkillVersionChanged({ draft, skill: original });
		const missingFields = SkillLibraryService.missingSkillFields({ draft, isNew, isVersionChanged });
		const toolGroups = ToolLibraryService.groupToolsBySource({ tools: catalogue });
		const categoryChoices = PlaybookLibraryService.categoryChoices({ categories });
		const idleSaveLabel = isNew ? "Create skill" : isVersionChanged ? "Save as a new version" : "Save";
		const saveLabel = isSaving ? "Saving…" : idleSaveLabel;

		return (
			<div className="agent-skill-editor">
				<div className="agent-skill-editor__columns">
					<div className="agent-skill-editor__main">
						<div className="tk-form-field agent-skill-editor__category">
							<div className="tk-form-label">
								Category<span className="tk-form-required">*</span>
							</div>
							<ToolsSelect
								value={draft.categoryId}
								options={categoryChoices}
								onChange={categoryId => onChange({ categoryId })}
								placeholder="The kind of work it serves…"
								disabled={isSaving}
							/>
						</div>
						<div className="tk-form-field">
							<div className="tk-form-label">
								Name<span className="tk-form-required">*</span>
							</div>
							<input
								className="tk-input"
								value={draft.name}
								onChange={event => this.onFieldChanged({ field: "name", event })}
								placeholder="e.g. Cash flow forecasting"
								maxLength={200}
								disabled={isSaving}
							/>
						</div>
						<div className="tk-form-field">
							<div className="tk-form-label">Description</div>
							<input
								className="tk-input"
								value={draft.description}
								onChange={event => this.onFieldChanged({ field: "description", event })}
								placeholder="One line: what this skill lets an agent do"
								maxLength={500}
								disabled={isSaving}
							/>
						</div>
						<div className="tk-form-field">
							<div className="tk-form-label">
								Instructions<span className="tk-form-required">*</span>
							</div>
							<textarea
								className="tk-input agent-skill-editor__body"
								value={draft.body}
								onChange={event => this.onFieldChanged({ field: "body", event })}
								rows={24}
								spellCheck
								disabled={isSaving}
							/>
							<div className="tk-form-help"># for a heading, - for a bullet, **bold** for emphasis. Agents read it as written.</div>
						</div>
					</div>
					<div className="agent-skill-editor__side">
						<div className="tk-form-field">
							<div className="tk-form-label">Tools it brings</div>
							<div className="tk-form-help">An agent with this skill can use these. Grouped by what each reads.</div>
							<div className="agent-skill-editor__tools">{toolGroups.map(this.renderToolGroup)}</div>
						</div>
					</div>
				</div>
				<div className="agent-skill-editor__footer">
					{isVersionChanged && (
						<div className="tk-form-field agent-skill-editor__note">
							<div className="tk-form-label">What changed and why{!isNew && <span className="tk-form-required">*</span>}</div>
							<input
								className="tk-input"
								value={draft.changeNote}
								onChange={event => this.onFieldChanged({ field: "changeNote", event })}
								placeholder={isNew ? "Optional for a new skill" : "e.g. Added the steps for seasonal businesses"}
								maxLength={500}
								disabled={isSaving}
							/>
						</div>
					)}
					{saveError && (
						<div className="tk-notice tk-notice--danger">
							<div className="tk-notice__title">
								<Icon.AlertTriangle size={14} />
								{saveError}
							</div>
						</div>
					)}
					{pendingReview &&
						this.renderReview({ pendingReview, authorizationReason, onAuthorizationReasonChange, onAuthorize, onDismissReview, isSaving })}
					{!pendingReview && (
						<div className="agent-skill-editor__actions">
							{missingFields.length > 0 && <span className="tk-muted">Still needed: {missingFields.join(", ")}</span>}
							<div className="tk-modal-actions">
								<button type="button" className="tk-button tk-button--secondary" onClick={onCancel} disabled={isSaving}>
									Cancel
								</button>
								<button type="button" className="tk-button tk-button--primary" onClick={onSave} disabled={missingFields.length > 0 || isSaving}>
									<Icon.Check size={14} />
									{saveLabel}
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	renderReview({ pendingReview, authorizationReason, onAuthorizationReasonChange, onAuthorize, onDismissReview, isSaving }) {
		const { review, playbooksUsing } = pendingReview;
		const described = SkillLibraryService.describeSkillReview({ review });
		return (
			<div className={`tk-notice tk-notice--${described.tone} agent-skill-editor__review`}>
				<div className="tk-notice__title">
					<Icon.AlertTriangle size={14} />
					{described.label}
				</div>
				<p className="agent-skill-editor__review-text">{review.reason}</p>
				{review.affects && <p className="agent-skill-editor__review-text">{review.affects}</p>}
				{playbooksUsing.length > 0 && <p className="agent-skill-editor__review-text">Playbooks that follow it: {playbooksUsing.join(", ")}.</p>}
				<div className="tk-form-field agent-skill-editor__note">
					<div className="tk-form-label">
						Why this change is right<span className="tk-form-required">*</span>
					</div>
					<input
						className="tk-input"
						value={authorizationReason}
						onChange={event => onAuthorizationReasonChange({ reason: event.target.value })}
						placeholder="Recorded on the version with your name"
						maxLength={1000}
						disabled={isSaving}
					/>
				</div>
				<div className="agent-skill-editor__review-actions">
					{review.reviewedWith && (
						<span className="tk-muted">
							Checked by {review.reviewedWith.skill} v{review.reviewedWith.version}
						</span>
					)}
					<div className="tk-modal-actions">
						<button type="button" className="tk-button tk-button--secondary" onClick={onDismissReview} disabled={isSaving}>
							Keep editing
						</button>
						<button type="button" className="tk-button tk-button--primary" onClick={onAuthorize} disabled={!authorizationReason.trim() || isSaving}>
							<Icon.Check size={14} />
							{isSaving ? "Saving…" : "Authorize and save"}
						</button>
					</div>
				</div>
			</div>
		);
	}
}

export default AgentSkillEditor;
